import { useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { EntitySheet } from "@/components/entity-sheet";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-expect-error — legacy JS, no type defs.
import AutocompleteInput from "../../autocomplete-input";
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-expect-error — legacy JS, no type defs.
import ImagePicker from "../../image-picker";
import { TagsMultiSelect } from "../../tags-multi-select";
import { useNotificationsProvider } from "../../../context/NotificationsContext";
import {
  useEditItemMutation,
  useGetTagsForEntityQuery,
  useSetTagsForEntityMutation,
  useUploadFileMutation,
} from "../../../services/api";

interface TagRef {
  id: number;
  name: string;
  color: string | null;
}

interface ParentRef {
  id?: string | number;
  title?: string;
  [key: string]: unknown;
}

export interface ItemRowForSheet {
  id: string | number;
  title?: string;
  description?: string;
  photos?: string[] | null;
  parentId?: string | number | null;
  tags?: TagRef[];
  [key: string]: unknown;
}

interface ItemSheetProps {
  value: ItemRowForSheet | null;
  parent: ParentRef | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  canEdit: boolean;
  initialEditing?: boolean;
  onEdited?: () => void;
  onDelete?: () => void;
}

interface FormValues {
  itemTitle: string;
  itemDescription: string;
  itemParent: ParentRef | null;
  selectedTags: TagRef[];
}

export function ItemSheet({
  value,
  parent,
  open,
  onOpenChange,
  canEdit,
  initialEditing = false,
  onEdited,
  onDelete,
}: ItemSheetProps) {
  const { t } = useTranslation();
  const { showNotification } = useNotificationsProvider();

  const [editItem, { isLoading: isEditing }] = useEditItemMutation();
  const [uploadFile, { isLoading: isUploading }] = useUploadFileMutation();
  const [setTags, { isLoading: isSettingTags }] = useSetTagsForEntityMutation();

  const { data: currentTags } = useGetTagsForEntityQuery(
    { taggableType: "Item", taggableId: value?.id },
    { skip: !open || !value?.id },
  ) as { data?: TagRef[] };

  const [editing, setEditing] = useState(false);
  // Images stay outside the form: they're File blobs in edit mode,
  // urls in read mode, and they upload separately to a different
  // mutation. Keeping them in a single useState avoids fighting RHF.
  const [images, setImages] = useState<string[] | null>(null);

  const schema = useMemo(
    () =>
      z.object({
        itemTitle: z
          .string()
          .trim()
          .min(1, t("items.errors.title-required", "Title is required")),
        itemDescription: z
          .string()
          .trim()
          .min(1, t("items.errors.description-required", "Description is required")),
        itemParent: z.any().nullable(),
        selectedTags: z.array(z.any()),
      }),
    [t],
  );

  const form = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      itemTitle: value?.title ?? "",
      itemDescription: value?.description ?? "",
      itemParent: parent,
      selectedTags: value?.tags ?? [],
    },
  });

  useEffect(() => {
    if (!value) return;
    form.reset({
      itemTitle: value.title ?? "",
      itemDescription: value.description ?? "",
      itemParent: parent,
      selectedTags: value.tags ?? [],
    });
    setImages(value.photos ?? null);
    setEditing(open ? initialEditing : false);
    // `form` is intentionally excluded: including it would cause a reset loop
    // because form.reset() itself triggers re-renders. Re-seed only when the
    // displayed entity changes or the sheet opens/closes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value?.id, open, initialEditing, parent]);

  useEffect(() => {
    if (Array.isArray(currentTags)) {
      form.setValue(
        "selectedTags",
        currentTags.map((tag) => ({
          id: tag.id,
          name: tag.name,
          color: tag.color,
        })),
      );
    } else if (value?.tags) {
      form.setValue("selectedTags", value.tags);
    }
    // `form` is intentionally excluded — same reset-loop reason as above.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentTags, value?.tags]);

  function handleEditError(err: unknown) {
    const errorCode = (err as {
      data?: { validationErrors?: { errorCode?: string }[] };
    })?.data?.validationErrors?.[0]?.errorCode;
    if (errorCode === "ITEM_DETAILS_ALREADY_USED") {
      showNotification("error", t("items.add-item-dialog.already-exists-error"));
    } else if (errorCode === "ITEM_LEVEL_EXCEEDED") {
      showNotification("error", t("items.add-item-dialog.level-exceeded-error"));
    } else if (errorCode === "ITEM_INVALID_DESCENDANT_AS_PARENT") {
      showNotification(
        "error",
        t("items.add-item-dialog.invalid-descendant-as-parent-error"),
      );
    } else {
      showNotification("error", t("common.api-error"));
    }
  }

  const onSubmit = async (values: FormValues) => {
    if (!value) return;
    const data: Record<string, unknown> = {
      title: values.itemTitle.trim(),
      description: values.itemDescription.trim(),
    };
    if (values.itemParent?.id) data.parentId = values.itemParent.id;

    try {
      if (images && images.length > 0) {
        const uploaded = await Promise.all(
          images.map((image) =>
            uploadFile({
              file: image,
              meta: { contentType: (image as unknown as File).type },
            }).unwrap(),
          ),
        );
        data.photosJson = JSON.stringify(
          (uploaded as { url: string }[]).map((r) => r.url),
        );
      }
      await editItem({ id: value.id, ...data }).unwrap();
      await setTags({
        taggableType: "Item",
        taggableId: value.id,
        tagIds: values.selectedTags.map((tag) => tag.id),
      }).unwrap();
      setEditing(false);
      onEdited?.();
    } catch (err) {
      handleEditError(err);
    }
  };

  const loading = isEditing || isUploading || isSettingTags;
  const currentForm = form.watch();

  return (
    <EntitySheet
      open={open}
      onOpenChange={onOpenChange}
      title={value?.title ?? t("sidebar.inspection-items")}
      description={value?.description}
      editing={editing}
      onToggleEdit={setEditing}
      canEdit={canEdit}
      onDelete={onDelete}
      footer={
        editing ? (
          <div className="flex justify-end gap-2">
            <Button
              variant="outline"
              onClick={() => setEditing(false)}
              disabled={loading}
            >
              {t("common.cancel")}
            </Button>
            <Button onClick={form.handleSubmit(onSubmit)} disabled={loading}>
              {t("common.save")}
            </Button>
          </div>
        ) : null
      }
    >
      {editing ? (
        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(onSubmit)}
            className="space-y-5"
            noValidate
          >
            <ImagePicker initialImages={images} onChange={setImages} />

            <FormField
              control={form.control}
              name="itemTitle"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    {t("items.edit-item-dialog.item-title")}
                  </FormLabel>
                  <FormControl>
                    <Input {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="itemDescription"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    {t("items.edit-item-dialog.description-title")}
                  </FormLabel>
                  <FormControl>
                    <Textarea rows={3} {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="itemParent"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    {t("items.add-item-dialog.parent-item-title")}
                  </FormLabel>
                  <AutocompleteInput
                    onChange={(val: ParentRef | null) => field.onChange(val)}
                    defaultValue={field.value}
                    value={field.value}
                    resource="items/autocomplete"
                    canDelete
                    refreshOnInputChange
                    clearOnBlur={false}
                    freeSolo
                  />
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="selectedTags"
              render={({ field }) => (
                <FormItem>
                  <TagsMultiSelect
                    value={field.value}
                    onChange={field.onChange}
                    label={t("items.edit-item-dialog.tags-title")}
                    placeholder={t("tags.multi-select.placeholder")}
                  />
                </FormItem>
              )}
            />
          </form>
        </Form>
      ) : (
        <ItemReadView
          itemTitle={currentForm.itemTitle}
          itemDescription={currentForm.itemDescription}
          itemParent={currentForm.itemParent}
          tags={currentForm.selectedTags}
          images={images}
          t={t}
        />
      )}
    </EntitySheet>
  );
}

function ItemReadView({
  itemTitle,
  itemDescription,
  itemParent,
  tags,
  images,
  t,
}: {
  itemTitle: string;
  itemDescription: string;
  itemParent: ParentRef | null;
  tags: TagRef[];
  images: string[] | null;
  t: ReturnType<typeof useTranslation>["t"];
}) {
  return (
    <div className="space-y-5">
      <dl className="space-y-3 text-sm">
        <div>
          <dt className="text-xs uppercase tracking-wide text-muted-foreground">
            {t("items.add-item-dialog.description-title")}
          </dt>
          <dd className="mt-1 whitespace-pre-wrap">{itemDescription || "—"}</dd>
        </div>
        {itemParent?.title ? (
          <div>
            <dt className="text-xs uppercase tracking-wide text-muted-foreground">
              {t("items.add-item-dialog.parent-item-title")}
            </dt>
            <dd className="mt-1">{itemParent.title}</dd>
          </div>
        ) : null}
      </dl>

      <section>
        <h3 className="mb-2 text-xs font-medium uppercase tracking-wide text-muted-foreground">
          {t("items.items.identifier")}
        </h3>
        <p className="text-sm">{itemTitle || "—"}</p>
      </section>

      <section>
        <h3 className="mb-2 text-xs font-medium uppercase tracking-wide text-muted-foreground">
          {t("resource.items.tags")}
        </h3>
        {tags.length > 0 ? (
          <div className="flex flex-wrap gap-1.5">
            {tags.map((tag) => (
              <Badge
                key={tag.id}
                style={{
                  backgroundColor: tag.color ?? undefined,
                  color: tag.color ? "#fff" : undefined,
                }}
              >
                {tag.name}
              </Badge>
            ))}
          </div>
        ) : (
          <p className="text-sm text-muted-foreground">{t("tags.empty")}</p>
        )}
      </section>

      {images && images.length > 0 ? (
        <section>
          <h3 className="mb-2 text-xs font-medium uppercase tracking-wide text-muted-foreground">
            {t("items.gallery.title")}
          </h3>
          <div className="grid grid-cols-3 gap-2">
            {images.map((src, idx) => (
              <img
                // eslint-disable-next-line react/no-array-index-key
                key={idx}
                src={src}
                alt=""
                className="size-24 rounded-md border border-border object-cover"
              />
            ))}
          </div>
        </section>
      ) : null}
    </div>
  );
}
